import React from 'react';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import DirectionsIcon from '@mui/icons-material/Directions';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { LocalAtRecord } from '../../../db';
import { distanceLatLon, round } from '../../../utils';
import { Tooltip } from '@mui/material';
import { alertFullScreen } from '../../../alertDispatcher';
import { FaReceipt } from 'react-icons/fa';
import { ShiftDropoffsFieldSet } from '@rogoag/airtable/crm';

interface ShippingDropoffProps {
  key: string;
  id: string;
  currentLat: number;
  currentLon: number;
  location: string;
}

export default function ShippingDropoff(props: ShippingDropoffProps) {
  const [dropLat, setDropLat] = React.useState<number>();
  const [dropLon, setDropLon] = React.useState<number>();
  const [hours, setHours] = React.useState<string>('');
  const [done, setDone] = React.useState<boolean>(false);
  const [notes, setNotes] = React.useState<string>('');
  const [name, setName] = React.useState<string>('');

  React.useEffect(() => {
    const dropoff = LocalAtRecord.getRecord<ShiftDropoffsFieldSet>('Shift Dropoffs', props.id);

    if (dropoff) {
      const dropoffLat = dropoff.get('Dropoff Lat');

      if (Array.isArray(dropoffLat) && dropoffLat.length > 0) {
        setDropLat(dropoffLat[0]);
      }

      const dropoffLon = dropoff.get('Dropoff Lon');

      if (Array.isArray(dropoffLon) && dropoffLon.length > 0) {
        setDropLon(dropoffLon[0]);
      }

      const dropoffHours = dropoff.get('Dropoff Hours');

      if (Array.isArray(dropoffHours) && dropoffHours.length > 0) {
        setHours(dropoffHours[0]);
      }

      setDone(dropoff.get('Dropoff Complete') ? true : false);
      const operatorNotes = dropoff.getFirst('Dropoff Notes') || '';
      setNotes(operatorNotes);

      setName((dropoff.get('ShiftDropoff Name for App') as string) || '');
    }
  }, [props.id]);

  const distanceMiles =
    !!dropLat && !!dropLon
      ? round(distanceLatLon(dropLat, dropLon, props.currentLat, props.currentLon, 'Miles'), 1)
      : undefined;
  const doneSx = done ? { backgroundColor: 'success.light', color: 'success.contrastText' } : undefined;

  return (
    <ListItem key={props.id} sx={doneSx} divider data-testid={`shipping-dropoff-${props.id}`}>
      <ListItemIcon>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }} // stack on mobile, row on larger screens
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          width="100%"
        >
          <Link
            href={`https://www.google.com/maps/dir/?api=1&destination="${props.location}"`}
            target="_blank"
            rel="noreferrer"
          >
            <IconButton color="secondary" size="large">
              <DirectionsIcon fontSize="large" />
            </IconButton>
          </Link>

          <Tooltip title={!notes ? 'No Dropoff Notes Available' : 'Dropoff Notes'} placement="top">
            <span>
              <IconButton
                disabled={!notes}
                color="secondary"
                onClick={async (e) => {
                  // the notes boolean property disables this component if it's not valid
                  await alertFullScreen('Dropoff Notes', notes!, ['Close']);
                }}
                size="large"
              >
                <FaReceipt fontSize={32} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </ListItemIcon>
      <Box pl={5} display="flex" justifyContent="space-between" alignItems="flex-start" width="100%">
        {/* Left Side: Dropoff info*/}
        <Box flexGrow={1} display="flex" flexDirection="column" justifyContent="start" alignItems="space-between">
          <Typography variant="body2">
            <b>Dropoff:</b> {props.location || ''}
          </Typography>

          <Typography variant="caption" style={{ fontSize: '0.75rem', color: '#0000FF' }}>
            {name}
          </Typography>
        </Box>

        {/* Right Side: hours and distance */}
        <Box display="flex" flexDirection={'column'} justifyContent="space-between" alignItems="flex-end">
          <Typography>
            <b>{hours || '\u00A0'}</b> {/* Non-breaking space when empty */}
          </Typography>

          <Typography style={{ textAlign: 'right', width: '100%' }}>
            <b>{distanceMiles || 'N/A'}</b> mi.
          </Typography>
        </Box>
      </Box>
    </ListItem>
  );
}
